import { useDispatch, useSelector, useStore } from "react-redux";

import { getDatasetMetadata } from "@/api/dataset/datasetApi";
import NotificationContent from "@/components/notification/notificationContent";
import { MESSAGE } from "@/constants/message";
import { useDataset } from "@/hooks/map/useDataset";
import useConfigurationMappers from "@/hooks/project/useConfigurationMapper";
import { useRestoreDataset } from "@/hooks/project/useRestoreDataset";
import {
  pruneConfigForMetadata,
  pruneStyleForSchema
} from "@/hooks/project/utils";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  recalculateWeightPercentages,
  setDatasetHasUpdatedAdmAreaMapping,
  setDatasetIsFetching
} from "@/store/slice/datasetSlice";
import { setWidgetOrder } from "@/store/slice/widgetSlice";
import { Dataset, DatasetStyle } from "@/types/dataset";
import { WidgetConfigWithoutWidgetId } from "@/types/widget";
import {
  convertDatasetResponseToFeatureCollection,
  isFeatureCollectionWGS84
} from "@/utils/dataset";

/** The custom legend as it is in the store, including images not saved yet */
const getLegendStyle = (style: DatasetStyle) => ({
  colorScaleMethod: style.colorScaleMethod,
  colorScaleSettings: style.colorScaleSettings,
  singleStyleConfig: style.singleStyleConfigSaved ?? style.singleStyleConfig,
  customCategoryConfig:
    style.customCategoryConfigSaved ?? style.customCategoryConfig,
  customRangeConfig: style.customRangeConfigSaved ?? style.customRangeConfig
});

/**
 * Reloads a dataset whose spatial aggregation was changed in Data Settings.
 * The dataset is added again with the new aggregation in the same place in the
 * list. Its filters, weight, styling and widgets are kept while they still fit
 * the new data.
 */
export const useRefreshUpdatedDataset = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { showToast } = useToast();
  const { isCustomMapVisualizationLegendEnabled } = useAppFeatures();
  const { handleAddDataset, handleRemoveDataset, handleReorderDataset } =
    useDataset();
  const { mapDatasetConfig } = useConfigurationMappers();
  const { fetchDatasetData, buildRestoredDatasetArgs } = useRestoreDataset();
  const visualizationOrder = useSelector(
    (state: RootState) => state.dataset.visualizationOrder
  );

  /**
   * The dataset's widgets, top to bottom. A dataset that is no longer time
   * series loses its grid and group by, so its widgets lose them too.
   */
  const getDatasetWidgets = (
    datasetId: string,
    isTimeseries: boolean
  ): WidgetConfigWithoutWidgetId[] => {
    const { widgets, visualizationOrder: widgetOrder } =
      store.getState().widget;
    return widgetOrder
      .map((widgetId) => widgets[widgetId])
      .filter((widget) => widget?.dataset_id === datasetId)
      .map(({ widget_id: _widgetId, ...widget }) =>
        isTimeseries
          ? widget
          : { ...widget, h3Configuration: undefined, group_by: undefined }
      );
  };

  /** Where the dataset's widgets sit in the widget panel, top to bottom */
  const getDatasetWidgetSlots = (datasetId: string) => {
    const { widgets, visualizationOrder: widgetOrder } =
      store.getState().widget;
    return widgetOrder.flatMap((widgetId, index) =>
      widgets[widgetId]?.dataset_id === datasetId ? [index] : []
    );
  };

  /**
   * Re-adding puts the dataset's widgets on top of the widget panel, so move
   * each one back to the slot its old widget had.
   */
  const restoreWidgetSlots = (datasetId: string, slots: number[]) => {
    const { widgets, visualizationOrder: widgetOrder } =
      store.getState().widget;
    const isDatasetWidget = (widgetId: string) =>
      widgets[widgetId]?.dataset_id === datasetId;
    const datasetWidgetIds = widgetOrder.filter(isDatasetWidget);
    const nextOrder = widgetOrder.filter(
      (widgetId) => !isDatasetWidget(widgetId)
    );
    slots.forEach((slot, index) =>
      nextOrder.splice(slot, 0, datasetWidgetIds[index])
    );
    dispatch(setWidgetOrder(nextOrder));
  };

  const refreshUpdatedDataset = async (dataset: Dataset) => {
    const datasetId = dataset.data.id;
    const [baseId, datasetIndex] = datasetId.split("_");
    const position = visualizationOrder.indexOf(datasetId);
    // Open Project keeps the saved config, as the dataset was never loaded.
    // Otherwise the current filters and styling are used.
    const { savedConfig } = dataset.properties;
    const currentConfig = savedConfig ?? mapDatasetConfig(datasetId, dataset);

    dispatch(setDatasetIsFetching({ datasetId, isFetching: true }));
    try {
      const metadata = await getDatasetMetadata(baseId);
      const config = pruneConfigForMetadata(currentConfig, metadata);
      const { aggregatedMetadata, data } = await fetchDatasetData(
        config,
        metadata
      );
      if (!data) {
        throw new Error(`Dataset ${baseId} returned no data`);
      }
      // Checked before removing the old dataset, so a failed refresh keeps it
      const featureCollection = convertDatasetResponseToFeatureCollection(
        data,
        aggregatedMetadata.schema
      );
      if (!isFeatureCollectionWGS84(featureCollection)) {
        throw new Error(`Dataset ${baseId} is not in WGS84`);
      }

      const styledConfig = pruneStyleForSchema(
        config,
        aggregatedMetadata.schema
      );
      const restoredArgs = await buildRestoredDatasetArgs(
        styledConfig,
        metadata,
        data,
        aggregatedMetadata.schema
      );
      // The saved config has no images the user added but did not save yet,
      // so the legend comes from the store while its attribute is still valid
      const shouldKeepLegendFromStore =
        isCustomMapVisualizationLegendEnabled &&
        !savedConfig &&
        styledConfig.color_based === config.color_based;
      const widgets = getDatasetWidgets(datasetId, !!metadata.timeseries);
      const widgetSlots = getDatasetWidgetSlots(datasetId);

      handleRemoveDataset(datasetId, dataset.properties.spatialType);
      await handleAddDataset({
        ...restoredArgs,
        styleConfig: {
          ...restoredArgs.styleConfig,
          ...(shouldKeepLegendFromStore ? getLegendStyle(dataset.style) : {})
        },
        datasetIndex: Number(datasetIndex),
        index: position,
        widget: widgets
      });
      dispatch(recalculateWeightPercentages());
      // Re-adding creates a fresh dataset without this flag, so copy it over.
      // Refresh only clears the spatial warning; the Adm. Area info stays.
      if (dataset.properties.hasUpdatedAdmAreaMapping) {
        dispatch(
          setDatasetHasUpdatedAdmAreaMapping({ datasetId, hasUpdated: true })
        );
      }

      // Re-adding puts the dataset at the top, so move it back to its old index
      if (position >= 0) handleReorderDataset(datasetId, position);
      restoreWidgetSlots(datasetId, widgetSlots);
    } catch {
      dispatch(setDatasetIsFetching({ datasetId, isFetching: false }));
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={MESSAGE.DATASET_ADD_TITLE_FAILED}
            description={`${MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX} ${dataset.data.display_name ?? dataset.data.name} ${MESSAGE.DATASET_ADD_DESCRIPTION_FAILED}`}
          />
        )
      });
    }
  };

  return { refreshUpdatedDataset };
};
